import { isEmpty } from 'lodash';
import { alpha } from '@mui/material';

import { CHART_TABLE_CELL_VARIANTS, sampleColors } from 'constants/dashboard';
import { setColorGrowing } from 'helper/dashboardColors';
import generateKey from 'helper/generateKey';

// region BUCKETS
const getBucketsTableData = (keyAreaQuestions) => ({
  header: ['', '', 'From Previous', 'All Time', 'Score'],
  columns: keyAreaQuestions.map((item) => ({
    id: generateKey(),
    cells: [
      {
        variant: CHART_TABLE_CELL_VARIANTS.CIRCLE,
        color: item?.color,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.LABEL,
        text: item.cultural_pillar,
        tooltip: item.statement,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.TREND,
        value: item?.trend_delta?.direction,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.TREND,
        value: item?.trend,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.SCORE,
        value: item?.avg_score,
      },
    ],
  })),
});

const getBucketsGraphData = (keyAreaQuestions) =>
  keyAreaQuestions.map((item) => ({
    name: item.cultural_pillar,
    value: item.avg_score,
    color: item.color,
  }));

export const formatBucketsData = (bucketsData) => {
  return !bucketsData?.length
    ? []
    : bucketsData.map((bucket) => ({
        name: bucket.name,
        id: bucket.id || bucket.key_area_ids,
        growing: bucket.growing,
        growing_color: setColorGrowing(bucket.growing),
        table: getBucketsTableData(bucket.key_area_questions),
        graph: getBucketsGraphData(bucket.key_area_questions),
      }));
};
// endregion

// region SAMPLE
const getSegmentColor = (index) => {
  const sampleColorsLength = sampleColors.length;

  if (index < sampleColorsLength) {
    return sampleColors[index];
  }

  const transparencyCoefficient = Math.floor(index / sampleColorsLength) * 0.25;
  const colorIndex = index % sampleColorsLength;

  return alpha(sampleColors[colorIndex], 1 - transparencyCoefficient);
};

const getSampleTableData = (groupData) => ({
  header: ['', '', 'Surveys'],
  columns: groupData.map((item, index) => ({
    id: generateKey(),
    cells: [
      {
        variant: CHART_TABLE_CELL_VARIANTS.CIRCLE,
        color: getSegmentColor(index),
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.LABEL,
        text: item.title,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.SCORE,
        value: item.people_count,
      },
    ],
  })),
});

const getSampleGraphData = (groupData) =>
  groupData.map((item, index) => ({
    name: item.title,
    value: item.people_count,
    color: getSegmentColor(index),
  }));

export const formatSampleData = (sampleData) =>
  sampleData.map(({ group_data: groupData, group_name: groupName } = {}) => ({
    groupName,
    table: getSampleTableData(groupData),
    graph: getSampleGraphData(groupData),
  }));
// endregion

// region CULTURAL
export const formatCulturalData = (cultureData = {}) => ({
  growing: cultureData.growing ?? null,
  participation: cultureData.participation ?? null,
  performance: cultureData.performance ?? 0,
  growing_color: cultureData.growing || cultureData.growing === 0 ? setColorGrowing(cultureData.growing) : '',
  answersNumber: cultureData.answers_number ?? null,
});
// endregion

// region COMMUNITY
const generateCommunityTableColumns = (data = []) =>
  data.map((item) => ({
    id: generateKey(),
    cells: [
      {
        variant: CHART_TABLE_CELL_VARIANTS.CIRCLE,
        color: item.color,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.LABEL,
        text: item.cultural_pillar,
        tooltip: item.statement,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.TREND,
        value: item.trend_delta?.direction,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.TREND,
        value: item.trend,
      },
      {
        variant: CHART_TABLE_CELL_VARIANTS.SCORE,
        value: item.avg_score,
      },
    ],
  }));

export const formatCommunityData = ({ lowest, highest } = {}) => {
  const header = ['', 'Pillars', 'From Previous', 'All Time', 'Score'];

  return {
    ...(lowest?.length && {
      challenge: {
        header,
        columns: generateCommunityTableColumns(lowest),
      },
    }),
    ...(highest?.length && {
      strength: {
        header,
        columns: generateCommunityTableColumns(highest),
      },
    }),
  };
};
// endregion

// region WORDS
export const formatWordsData = (words) =>
  isEmpty(words)
    ? []
    : Object.keys(words).map((word) => ({
        text: word,
        value: words[word].value,
        versions: words[word].not_corrected ?? [],
      }));

export const formatWordCloudData = (wordCloud = {}) => ({
  allDataSets: formatWordsData(wordCloud.all_data),
  currentDataSet: formatWordsData(wordCloud.current_data),
});
// endregion

// region MASTER DASHBOARD
export const formatMasterDashboardData = (data) => ({
  id: data.id,
  name: data.name,
  projects: data.project_details.map((project) => ({
    id: project.id,
    name: project.name,
    status: project.status,
    launchDate: project.launch_date,
    createdAt: project.created_at,
    isPublic: project.is_public,
  })),
  dashboardData: {
    cultural: formatCulturalData(data.dashboard_data.cultural_performance || {}),
    sampleArr: formatSampleData(data.dashboard_data.groups ?? []),
    buckets: formatBucketsData(data.dashboard_data.buckets || []),
    community: formatCommunityData(data.dashboard_data.community_insights || {}),
    words: formatWordCloudData(data.dashboard_data.words),
  },
});
// endregion
